/**
 * @fileoverview Affiliate fee working table (MOR-438)
 *
 * Shared by the per-agreement breakdown and the per-firm drill-down, so both
 * surfaces show the same audit trail for the same month. The backend already
 * guarantees the numbers match (one `computeAffiliateFeeRows`); this keeps the
 * presentation from drifting too.
 *
 * Each row shows the derivation left to right, but intermediate columns that
 * are empty for every month (vendor cost, margin, adjustment) are dropped
 * instead of rendering as walls of dashes — the common no-adjustment case is
 * simply gross → rate → fee.
 *
 * Amounts are AUD, ex GST (GST is never affiliate-shareable revenue).
 */

import { Fragment, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatAudExact, formatPeriod } from "./billingFormat";
import { ChargeStatusPill } from "./billingShared";

export interface AffiliateFeeRow {
  periodYearMonth: string;
  monthsSinceConversion: number;
  ratePercent: number | null;
  grossAudCents: number;
  costAudCents: number;
  marginPercent: number | null;
  baseFeeAudCents: number;
  adjustmentAudCents: number;
  adjustedGrossAudCents: number;
  feeAudCents: number;
  churned: boolean;
  charges: AffiliateChargeLine[];
}

/** One billed item making up a month's gross. */
export interface AffiliateChargeLine {
  chargeId: string;
  kind: "subscription" | "per_job";
  description: string;
  jobLabel: string | null;
  jobCode: string | null;
  amountExGstAudCents: number;
  status: string;
}

export interface AffiliateSubtotals {
  grossAudCents: number;
  costAudCents: number;
  adjustmentAudCents: number;
  feeAudCents: number;
}

/** Charge-kind dot matching the revenue charts (teal recurring, orange jobs). */
function KindDot({ kind }: { kind: AffiliateChargeLine["kind"] }) {
  return (
    <span
      className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        kind === "subscription" ? "bg-teal-600" : "bg-orange-500",
      )}
    />
  );
}

export function AffiliateFeeTable({
  rows,
  subtotals,
}: {
  rows: AffiliateFeeRow[];
  subtotals?: AffiliateSubtotals;
}) {
  // Months start expanded when there are few, so the detail is visible
  // without a click on the common single-month case.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(rows.length <= 3 ? rows.map((r) => r.periodYearMonth) : []),
  );

  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
        No months have accrued yet.
      </p>
    );
  }

  // Only surface intermediate columns that carry data somewhere.
  const hasCost = rows.some((r) => r.costAudCents > 0);
  const hasAdjustment = rows.some((r) => r.adjustmentAudCents > 0);
  const detailColumns = 4 + (hasCost ? 2 : 0) + (hasAdjustment ? 2 : 0) + 1;

  const toggle = (period: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(period)) next.delete(period);
      else next.add(period);
      return next;
    });

  return (
    <div className="min-w-0 overflow-x-auto rounded-lg border">
      <Table containerClassName="min-w-0">
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead>Period</TableHead>
            <TableHead className="text-right">Mo</TableHead>
            <TableHead className="text-right">Gross</TableHead>
            {hasCost && (
              <>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Margin</TableHead>
              </>
            )}
            <TableHead className="text-right">Rate</TableHead>
            {hasAdjustment && (
              <>
                <TableHead className="text-right">Base fee</TableHead>
                <TableHead className="text-right">Adjustment</TableHead>
              </>
            )}
            <TableHead className="text-right">Fee</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const isOpen = expanded.has(row.periodYearMonth);
            return (
              <Fragment key={row.periodYearMonth}>
                <TableRow
                  className={row.charges.length > 0 ? "cursor-pointer" : ""}
                  onClick={() =>
                    row.charges.length > 0 && toggle(row.periodYearMonth)
                  }
                >
                  <TableCell className="whitespace-nowrap font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {row.charges.length > 0 &&
                        (isOpen ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        ))}
                      {formatPeriod(row.periodYearMonth)}
                      {row.churned && (
                        <Badge color="red" variant="soft" size="sm">
                          Churned
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.monthsSinceConversion}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatAudExact(row.grossAudCents)}
                  </TableCell>
                  {hasCost && (
                    <>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {row.costAudCents > 0
                          ? formatAudExact(row.costAudCents)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.marginPercent === null
                          ? "—"
                          : `${row.marginPercent}%`}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right tabular-nums">
                    {row.ratePercent === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      `${row.ratePercent}%`
                    )}
                  </TableCell>
                  {hasAdjustment && (
                    <>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {row.baseFeeAudCents > 0
                          ? formatAudExact(row.baseFeeAudCents)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.adjustmentAudCents > 0
                          ? `−${formatAudExact(row.adjustmentAudCents)}`
                          : "—"}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatAudExact(row.feeAudCents)}
                  </TableCell>
                </TableRow>

                {/* What actually made up this month's gross. */}
                {isOpen && (
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableCell colSpan={detailColumns} className="p-0">
                      <div className="flex flex-col divide-y divide-border/60 py-1 pl-9 pr-4">
                        {row.charges.map((line) => (
                          <div
                            key={line.chargeId}
                            className="flex items-center gap-3 py-2"
                          >
                            <KindDot kind={line.kind} />
                            <div className="min-w-0 flex-1">
                              <span className="text-xs font-medium">
                                {line.kind === "subscription"
                                  ? "Subscription"
                                  : "Per-job fee"}
                              </span>
                              <span className="ml-2 truncate text-xs text-muted-foreground">
                                {line.jobLabel
                                  ? `${line.jobCode ? `${line.jobCode} — ` : ""}${line.jobLabel}`
                                  : line.description}
                              </span>
                            </div>
                            <ChargeStatusPill status={line.status} />
                            <span className="w-24 text-right text-xs tabular-nums">
                              {formatAudExact(line.amountExGstAudCents)}
                            </span>
                            <span
                              className="w-24 text-right text-xs tabular-nums text-muted-foreground"
                              title="Share of this month's fee attributable to this charge"
                            >
                              {row.grossAudCents > 0 && row.feeAudCents > 0
                                ? formatAudExact(
                                    Math.round(
                                      (line.amountExGstAudCents /
                                        row.grossAudCents) *
                                        row.feeAudCents,
                                    ),
                                  )
                                : "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
          {subtotals && (
            <TableRow className="border-t-2 bg-muted/20 font-medium hover:bg-muted/20">
              <TableCell colSpan={2}>Subtotal</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatAudExact(subtotals.grossAudCents)}
              </TableCell>
              {hasCost && (
                <>
                  <TableCell className="text-right tabular-nums">
                    {subtotals.costAudCents > 0
                      ? formatAudExact(subtotals.costAudCents)
                      : "—"}
                  </TableCell>
                  <TableCell />
                </>
              )}
              <TableCell />
              {hasAdjustment && (
                <>
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {subtotals.adjustmentAudCents > 0
                      ? `−${formatAudExact(subtotals.adjustmentAudCents)}`
                      : "—"}
                  </TableCell>
                </>
              )}
              <TableCell className="text-right font-semibold tabular-nums">
                {formatAudExact(subtotals.feeAudCents)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
